/**
 * Shared element renderers: text, image and sound of a schema `Element`.
 */
import { useCallback, useLayoutEffect, useRef, type CSSProperties, type ReactNode, type RefObject } from "react";
import { TEXT_SIZE_PX, type Element as El, type Text, type TextAlign } from "@makeit/schema";
import { libraryUrl, mediaUrl } from "../data/media";
import { usePlayer } from "./context";
import { fontSizeAdjust } from "./fonts";
import { dirOf } from "./rtl";

const ALIGN: Record<TextAlign, { justify: string; align: string; textAlign: CSSProperties["textAlign"] }> = {
  upperLeft: { justify: "flex-start", align: "flex-start", textAlign: "left" },
  upperCenter: { justify: "flex-start", align: "center", textAlign: "center" },
  upperRight: { justify: "flex-start", align: "flex-end", textAlign: "right" },
  middleLeft: { justify: "center", align: "flex-start", textAlign: "left" },
  middleCenter: { justify: "center", align: "center", textAlign: "center" },
  middleRight: { justify: "center", align: "flex-end", textAlign: "right" },
  lowerLeft: { justify: "flex-end", align: "flex-start", textAlign: "left" },
  lowerCenter: { justify: "flex-end", align: "center", textAlign: "center" },
  lowerRight: { justify: "flex-end", align: "flex-end", textAlign: "right" },
};

/**
 * Unity `Text.resizeTextForBestFit`: the largest font size in [min, max] at
 * which the text, wrapped at word boundaries, fits the box: a word wider than
 * the box shrinks the text instead of breaking ("Crisàlid/e"), as in the apps'
 * Fonts.bestFit. Only a word too wide even at `min` breaks after all.
 * Binary search on the real layout; re-run when the text or the box size changes.
 */
export function useBestFit(ref: RefObject<HTMLDivElement | null>, enabled: boolean, text: string, min: number, max: number) {
  useLayoutEffect(() => {
    const el = ref.current;
    if (!enabled || !el) return;
    const fits = (size: number) => {
      el.style.fontSize = `${size}px`;
      return el.scrollWidth <= el.clientWidth + 1 && el.scrollHeight <= el.clientHeight + 1;
    };
    const run = () => {
      el.style.overflowWrap = "normal";
      let lo = min, hi = max;
      while (hi - lo > 1) {
        const mid = Math.floor((lo + hi) / 2);
        if (fits(mid)) lo = mid;
        else hi = mid;
      }
      // fits() leaves the size it tried set
      if (!fits(fits(hi) ? hi : lo)) el.style.overflowWrap = "anywhere";
    };
    run();
    const ro = new ResizeObserver(run);
    ro.observe(el);
    // fonts may arrive later and change the metrics
    document.fonts?.ready.then(run).catch(() => undefined);
    return () => ro.disconnect();
  }, [ref, enabled, text, min, max]);
}

export function TextBox({ text, className, style, fit, bestFit }: { text: Text; className?: string; style?: CSSProperties; fit?: boolean; /** Unity best fit up to this size (PlayerFreeElement / PlayerTapElement: 300) */ bestFit?: number }) {
  const { project } = usePlayer();
  const ref = useRef<HTMLDivElement>(null);
  const size = TEXT_SIZE_PX[text.size] + fontSizeAdjust(project.settings.font);
  // `fit`: the editor size, shrunk only if it does not fit the box.
  // `bestFit`: Unity best fit, grows up to that size (free / tapIt elements).
  useBestFit(ref, !!bestFit || !!fit, text.content, 10, bestFit ?? size);
  if (!text.content) return null;
  const a = ALIGN[text.align];
  return (
    <div
      ref={ref}
      className={`textbox ${className ?? ""}`}
      dir={dirOf(text.content)}
      style={{
        display: "flex",
        flexDirection: "column",
        justifyContent: a.justify,
        alignItems: a.align,
        textAlign: a.textAlign,
        color: text.color,
        fontSize: size,
        lineHeight: 1.15,
        padding: 20,
        boxSizing: "border-box",
        width: "100%",
        height: "100%",
        overflow: "hidden",
        // fitted text keeps its words whole (useBestFit shrinks it instead)
        wordBreak: bestFit || fit ? "normal" : "break-word",
        whiteSpace: "pre-wrap",
        unicodeBidi: "plaintext",
        ...style,
      }}
    >
      {text.content}
    </div>
  );
}

export function ElementImage({ element, className, style }: { element: El; className?: string; style?: CSSProperties }) {
  const { project } = usePlayer();
  const src = mediaUrl(element.image, "image", project.id);
  if (!src) return null;
  return (
    <img
      className={`element-image ${className ?? ""}`}
      src={src}
      alt=""
      draggable={false}
      style={{ width: "100%", height: "100%", objectFit: "contain", pointerEvents: "none", ...style }}
    />
  );
}

/** Plays the element's sound on demand. Returns a play() function. */
export function useElementSound(element: El | null | undefined) {
  const { project } = usePlayer();
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const src = element ? mediaUrl(element.sound, "sound", project.id) : null;
  const play = useCallback(() => {
    if (!src) return;
    if (!audioRef.current || audioRef.current.src !== src) {
      audioRef.current = new Audio(src);
    }
    audioRef.current.currentTime = 0;
    void audioRef.current.play().catch(() => undefined);
  }, [src]);
  return { play, hasSound: !!src };
}

/** Item backgrounds of the Unity app (Resources/backgrounds). */
export function backgroundUrl(name: "default_item" | "bgOrange" | "bgViolet" | "bgGreen" | "bgBlue" | "memory" | "M_Container-01"): string {
  return libraryUrl(`backgrounds/${name}.png`);
}

/**
 * Generic card: image + text stacked, used by most activities.
 *  - default: white rounded card (used by quizzes in quiz projects, chips…)
 *  - bg="item": the app's grey "default_item" frame (order / sequences / connect)
 *  - bg="none": no card at all; elements with an image always show just the
 *    image (PlayerElement.SetImage makes the frame transparent).
 */
export function ElementCard({ element, children, className, style, onClick, bg, fit = true }: { element: El; children?: ReactNode; className?: string; style?: CSSProperties; onClick?: () => void; bg?: "item" | "none"; /** false = Unity Overflow: the editor size, even past the card */ fit?: boolean }) {
  const hasImage = !!element.image?.fileName;
  const frame: CSSProperties | undefined =
    hasImage || bg === "none"
      ? { background: "transparent", boxShadow: "none" }
      : bg === "item"
        ? { backgroundImage: `url("${backgroundUrl("default_item")}")`, backgroundSize: "100% 100%", backgroundColor: "transparent", boxShadow: "none", borderRadius: 0 }
        : undefined;
  return (
    <div className={`element-card ${hasImage ? "with-image" : "text-only"} ${className ?? ""}`} data-id={element.id} style={{ ...frame, ...style }} onClick={onClick}>
      {hasImage && <ElementImage element={element} />}
      <TextBox
        text={element.text}
        style={{ ...(hasImage ? { position: "absolute", inset: 0 } : undefined), ...(fit ? undefined : { position: "absolute", inset: 0, whiteSpace: "pre", wordBreak: "normal", overflow: "visible", padding: 0 }) }}
        fit={fit}
      />
      {children}
    </div>
  );
}
